import { ref, computed } from "@vue/composition-api";
import axios from "@/libs/axios";
import axiosV1 from "@/libs/axiosV1";
import useFilters from "@/views/modules/contacts/useFilters";
import { resolveToPhoneNumber } from "@/utils/phoneNumber";

const contacts = ref([]);
const count = ref(0);
const metadataFields = ref([]);

export default function useContacts() {
    const { perPage, page, isSortDirDesc, sortBy, searchQuery, filters } = useFilters();
    // Default to not break all places which already used this
    const defaultParameters = computed(() => {
        const queryParams = {
            fields: "name,phone,email,company,reference,isHighPriority,isBlocked,isOptedOutOfEngage,unformattedPhone",
            s: {
                $and: [
                    {
                        $or: [
                            { name: { $cont: searchQuery.value } },
                            {
                                phone: {
                                    $cont: resolveToPhoneNumber(searchQuery.value) ?? searchQuery.value,
                                },
                            },
                            { email: { $cont: searchQuery.value } },
                            { company: { $cont: searchQuery.value } },
                            { reference: { $cont: searchQuery.value } },
                            { unformattedPhone: { $cont: searchQuery.value?.replace(/^0+|[^a-zA-Z0-9 ]/g, "") } },
                        ],
                    },
                ],
            },
            limit: perPage.value,
            page: page.value,
            sort: `${sortBy.value},${isSortDirDesc.value === true ? "DESC" : "ASC"}`,
        };

        if (filters.value.labels?.length) {
            queryParams.join = "labels";
        }

        if (filters.value.labels.length) {
            if (filters.value.filterOption === "or") {
                queryParams.s.$and.push({
                    "labels.id": {
                        $in: filters.value.labels,
                    },
                });
            } else {
                queryParams.s.$and.push({
                    "labels.id": {
                        $all: filters.value.labels,
                    },
                });
            }
        }

        return queryParams;
    });
    const isLoading = ref(true);
    const isSaving = ref(false);
    const defaultCallback = (response) => {
        contacts.value = response.data.data;
        //refTable.value = response;
        count.value = response.data.total;
    };

    const fetchMetadataFields = () => {
        axios
            .get("/contacts/metadata", {
                params: {
                    distinct: true,
                    fields: "name",
                },
            })
            .then((response) => (metadataFields.value = response.data));
    };

    const fetchContacts = (params = defaultParameters.value, callback = defaultCallback) => {
        isLoading.value = true;

        return axios
            .get("/contacts", {
                params,
            })
            .then(callback)
            .finally(() => (isLoading.value = false));
    };

    const fetchContact = (contactId, queryParams) => {
        isLoading.value = true;

        return axios.get(`/contacts/${contactId}`, { params: queryParams }).finally(() => (isLoading.value = false));
    };

    const createContact = (contact) => {
        isSaving.value = true;

        return _checkDuplicates(contact).then(() => {
            return axios.post("/contacts", contact).finally(() => (isSaving.value = false));
        });
    };

    const updateContact = (contact, originalContact) => {
        isSaving.value = true;

        return _checkDuplicates(contact, originalContact)
            .then(() => {
                return axios.patch(`/contacts/${contact.id}`, contact);
            })
            .finally(() => (isSaving.value = false));
    };

    const deleteContact = async (contactId) => {
        // @NOTE directly on API gateway.
        const { data } = await axiosV1.post(`/contacts/remove/${contactId}`, null, {
            baseURL: `${process.env.VUE_APP_API_GATEWAY.replace("v1", "v2")}`,
        });

        if (data.success) {
            const contactIndex = contacts.value.findIndex((contact) => contact.id === contactId);
            if (contactIndex !== -1) {
                contacts.value.splice(contactIndex, 1);
            }
        }
    };

    const _checkDuplicates = async (contact, originalContact) => {
        return new Promise(async (resolve, reject) => {
            // We do accept contacts without phone AND email.
            if (!contact.phone && !contact.email) {
                return resolve();
            }

            let searchQuery = [];

            // Existing contact data has no updated phone or email? Skip duplicate check.
            if (originalContact) {
                if (contact.email === originalContact.email && contact.phone === originalContact.phone) {
                    return resolve();
                }

                if (contact.phone !== originalContact.phone) {
                    searchQuery.push({ phone: contact.phone });
                }

                if (contact.email !== originalContact.email) {
                    searchQuery.push({ email: contact.email });
                }
            } else {
                if (contact.phone) {
                    searchQuery.push({ phone: contact.phone });
                }

                if (contact.email) {
                    searchQuery.push({ email: contact.email });
                }
            }

            const duplicateContacts = await axios.get("/contacts", {
                params: {
                    s: {
                        $or: searchQuery,
                    },
                },
            });

            if (duplicateContacts.data.length > 0) {
                return reject("contact-already-exists");
            }

            return resolve();
        });
    };

    return {
        contacts,
        count,
        metadataFields,
        filters,
        isLoading,
        isSaving,

        // Methods
        fetchContacts,
        fetchContact,
        createContact,
        updateContact,
        fetchMetadataFields,
        deleteContact,
    };
}
